import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Typography,
	Row,
	Col,
	Space,
	notification,
	Divider,
	Table,
	InputNumber,
	Checkbox,
	Tag,
	Popover,
} from "antd";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	PiShoppingCartDuotone,
	PiArrowLeftDuotone,
	PiPlusDuotone,
	PiTrashDuotone,
} from "react-icons/pi";
import {
	requestCreateOrder,
	requestGetUserDetail,
	requestGetServiceList,
	requestGetTeamList,
	requestGetDomainExtensionList,
} from "../../services/admin.service";
import { formatCurrency } from "../../utils/Formatter";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

function AddOrderPage() {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [servicesLoading, setServicesLoading] = useState(false);
	const [teamsLoading, setTeamsLoading] = useState(false);

	const [services, setServices] = useState([]);
	const [domainExtensions, setDomainExtensions] = useState([]);
	const [teams, setTeams] = useState([]);
	const [selectedTeamOwner, setSelectedTeamOwner] = useState(null);
	const [orderItems, setOrderItems] = useState([
		{
			key: Date.now(),
			type: "INSTANCE", // INSTANCE | DOMAIN
			serviceCode: "",
			planCode: "",
			regionCode: "",
			extensionCode: "",
			domainName: "",
			quantity: 1,
			addons: [],
			metadata: {},
		},
	]);

	const fetchInitialData = useCallback(async () => {
		await Promise.all([fetchServices(), fetchTeams(), fetchDomainExtensions()]);
	}, []);

	useEffect(() => {
		fetchInitialData();
	}, [fetchInitialData]);

	const fetchServices = () => {
		setServicesLoading(true);
		requestGetServiceList({ limit: 1000 })
			.then((response) => {
				const serviceData = response.data.data || response.data;
				setServices(serviceData);
			})
			.catch((error) => {
				console.error("Error fetching services:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch services",
				});
			})
			.finally(() => {
				setServicesLoading(false);
			});
	};

	const fetchTeams = () => {
		setTeamsLoading(true);
		requestGetTeamList({ limit: 1000 })
			.then((response) => {
				const teamData = response.data.data || response.data;
				setTeams(teamData);
			})
			.catch((error) => {
				console.error("Error fetching teams:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch teams",
				});
			})
			.finally(() => {
				setTeamsLoading(false);
			});
	};

	const fetchDomainExtensions = () => {
		requestGetDomainExtensionList({ limit: 1000 })
			.then((response) => {
				const data = response.data.data || response.data;
				setDomainExtensions(data);
			})
			.catch((error) => {
				console.error("Error fetching domain extensions:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch domain extensions",
				});
			});
	};

	const handleTeamSelect = async (teamId) => {
		const team = teams.find((t) => t._id === teamId);
		if (team && team.owner) {
			try {
				const response = await requestGetUserDetail(team.ownerId);
				const ownerData = response.data.data || response.data;
				setSelectedTeamOwner(ownerData);

				// Auto-fill billing address from team owner info
				form.setFieldsValue({
					billingName: ownerData.name,
					billingEmail: ownerData.email,
					billingPhone: ownerData.phone,
					// Add other fields if available in user data
					billingAddress: ownerData.address || "",
					billingCity: ownerData.city || "",
					billingState: ownerData.state || "",
					billingPostalCode: ownerData.postalCode || "",
					billingCountry: ownerData.country || "",
				});
			} catch (error) {
				console.error("Error fetching team owner details:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch team owner details",
				});
			}
		}
	};

	const handleSubmit = (values) => {
		setLoading(true);

		// Validate that we have at least one item
		if (
			orderItems.length === 0 ||
			orderItems.some((it) =>
				it.type === "INSTANCE"
					? !it.serviceCode || !it.planCode
					: !it.extensionCode || !it.domainName
			)
		) {
			notification.error({
				message: "Error",
				description: "Please complete all order items (service+plan or extension+domain)",
			});
			setLoading(false);
			return;
		}

		// Validate if there's order item with INSTANCE type, it must have domain in its metadata
		const hasInstanceWithoutDomain = orderItems.some(
			(item) => item.type === "INSTANCE" && !item.metadata.domain
		);
		if (hasInstanceWithoutDomain) {
			notification.error({
				message: "Error",
				description: "Instance type items must have a domain specified",
			});
			setLoading(false);
			return;
		}

		const orderData = {
			teamId: values.teamId,
			currency: values.currency,
			items: orderItems.map((item) => {
				if (item.type === "DOMAIN") {
					return {
						type: "DOMAIN",
						extensionCode: item.extensionCode,
						domainName: item.domainName,
						quantity: item.quantity, // interpreted as years
					};
				}
				return {
					type: "INSTANCE",
					serviceCode: item.serviceCode,
					planCode: item.planCode,
					regionCode: item.regionCode,
					addonCodes: item.addons?.map((addon) => addon.code) || [],
					quantity: item.quantity,
					metadata: item.metadata || {},
				};
			}),
			billingAddress: {
				name: values.billingName,
				email: values.billingEmail,
				phone: values.billingPhone,
				address: values.billingAddress,
				city: values.billingCity,
				state: values.billingState,
				postalCode: values.billingPostalCode,
				country: values.billingCountry,
			},
			metadata: values.metadata ? JSON.parse(values.metadata) : {},
		};

		requestCreateOrder(orderData)
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Order created successfully",
				});
				navigate("/orders");
			})
			.catch((error) => {
				console.error("Error creating order:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to create order. Please try again.",
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const validateJSON = (_, value) => {
		if (!value) return Promise.resolve();
		try {
			JSON.parse(value);
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(new Error("Please enter valid JSON"));
		}
	};

	const addOrderItem = () => {
		setOrderItems([
			...orderItems,
			{
				key: Date.now(),
				type: "INSTANCE",
				serviceCode: "",
				planCode: "",
				regionCode: "",
				extensionCode: "",
				domainName: "",
				quantity: 1,
				addons: [],
				metadata: {},
			},
		]);
	};

	const removeOrderItem = (key) => {
		setOrderItems(orderItems.filter((item) => item.key !== key));
	};

	const updateOrderItem = (key, field, value) => {
		setOrderItems(
			orderItems.map((item) => {
				if (item.key === key) {
					let updatedItem = { ...item, [field]: value };

					// When switching type, reset incompatible fields
					if (field === "type") {
						if (value === "DOMAIN") {
							updatedItem = {
								...updatedItem,
								serviceCode: "",
								planCode: "",
								regionCode: "",
								addons: [],
								metadata: {},
							};
						} else if (value === "INSTANCE") {
							updatedItem = {
								...updatedItem,
								extensionCode: "",
								domainName: "",
								metadata: updatedItem.metadata || {},
							};
						}
					}

					// Reset addons when service changes
					if (field === "serviceCode") {
						updatedItem.addons = [];
					}

					return updatedItem;
				}
				return item;
			})
		);
	};

	const getServicePlans = (serviceCode) => {
		const service = services.find((s) => s.code === serviceCode);
		return service?.plans || [];
	};

	const getServiceRegions = (serviceCode) => {
		const service = services.find((s) => s.code === serviceCode);
		return service?.regions || [];
	};

	const getServiceAddons = (serviceCode) => {
		const service = services.find((s) => s.code === serviceCode);
		return service?.addons || [];
	};

	const updateItemAddons = (itemKey, addons) => {
		setOrderItems(
			orderItems.map((item) => (item.key === itemKey ? { ...item, addons: addons } : item))
		);
	};

	const updateItemMetadata = (itemKey, field, value) => {
		setOrderItems(
			orderItems.map((item) => 
				item.key === itemKey 
					? { ...item, metadata: { ...item.metadata, [field]: value } } 
					: item
			)
		);
	};

	const calculateItemPrice = (item) => {
		if (item.type === "DOMAIN") {
			if (!item.extensionCode) return 0;
			const ext = domainExtensions.find((d) => d.code === item.extensionCode);
			if (!ext) return 0;
			const base = ext.price?.registration?.amount || 0;
			return base * (item.quantity || 1); // quantity interpreted as years
		}

		if (!item.serviceCode || !item.planCode) return 0;

		const service = services.find((s) => s.code === item.serviceCode);
		if (!service) return 0;

		const plan = service.plans?.find((p) => p.code === item.planCode);
		if (!plan) return 0;

		// Base price from plan
		let basePrice = plan.price?.amount || 0;

		// Calculate region price
		let regionPrice = 0;
		if (item.regionCode) {
			const region = service.regions?.find((r) => r.code === item.regionCode);
			if (region && region.price) {
				if (region.price.type === "FIXED") {
					regionPrice = region.price.amount || 0;
				} else if (region.price.type === "PERCENTAGE") {
					regionPrice = (basePrice * (region.price.amount || 0)) / 100;
				}
			}
		}

		// Current line price (base + region)
		const linePriceWithRegion = basePrice + regionPrice;

		// Calculate addon prices
		let addonPrice = 0;
		if (item.addons && item.addons.length > 0) {
			addonPrice = item.addons.reduce((total, addon) => {
				if (!addon.price) return total;

				if (addon.price.type === "FIXED") {
					return total + (addon.price.amount || 0);
				} else if (addon.price.type === "PERCENTAGE") {
					// Percentage of (line price + region price)
					return total + (linePriceWithRegion * (addon.price.amount || 0)) / 100;
				}

				// Fallback to old pricing structure if new structure not available
				return total + (addon.pricing?.basePrice || 0);
			}, 0);
		}

		return (linePriceWithRegion + addonPrice) * item.quantity;
	};

	const calculateOrderSummary = () => {
		const currency = form.getFieldValue("currency") || "USD";
		let subtotal = 0;
		let totalSetupFees = 0;

		orderItems.forEach((item) => {
			// Calculate item subtotal
			subtotal += calculateItemPrice(item);

			if (item.type === "INSTANCE" && item.serviceCode && item.planCode) {
				const service = services.find((s) => s.code === item.serviceCode);
				const plan = service?.plans?.find((p) => p.code === item.planCode);
				let setupFees = plan?.price?.setupFee || 0;

				// Add region setup fees
				if (item.regionCode) {
					const region = service?.regions?.find((r) => r.code === item.regionCode);
					if (region && region.price && region.price.setupFee) {
						setupFees += region.price.setupFee;
					}
				}

				// Add addon setup fees
				const addonSetupFees =
					item.addons?.reduce((total, addon) => {
						if (addon.price && addon.price.setupFee) {
							return total + addon.price.setupFee;
						}
						return total + (addon.pricing?.setupFee || 0);
					}, 0) || 0;

				totalSetupFees += (setupFees + addonSetupFees) * item.quantity;
			}
		});

		const total = subtotal + totalSetupFees;

		return {
			currency,
			subtotal,
			setupFees: totalSetupFees,
			total,
			itemCount: orderItems.filter(
				(item) =>
					(item.type === "INSTANCE" && item.serviceCode && item.planCode) ||
					(item.type === "DOMAIN" && item.extensionCode && item.domainName)
			).length,
		};
	};

	const orderItemColumns = [
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 140,
			render: (value, record) => (
				<Select
					value={value}
					style={{ width: "100%" }}
					onChange={(val) => updateOrderItem(record.key, "type", val)}>
					<Option value="INSTANCE">Instance</Option>
					<Option value="DOMAIN">Domain</Option>
				</Select>
			),
		},
		{
			title: (record) => (record?.type === "DOMAIN" ? "Extension" : "Service"),
			dataIndex: "serviceOrExtension",
			key: "serviceOrExtension",
			render: (_, record) => {
				if (record.type === "DOMAIN") {
					return (
						<Select
							value={record.extensionCode}
							placeholder="Select extension"
							style={{ width: "100%" }}
							showSearch
							optionFilterProp="children"
							onChange={(code) => updateOrderItem(record.key, "extensionCode", code)}>
							{domainExtensions.map((ext) => (
								<Option key={ext.code} value={ext.code}>
									{ext.name} ({ext.code})
								</Option>
							))}
						</Select>
					);
				}
				return (
					<Select
						value={record.serviceCode}
						placeholder="Select service"
						style={{ width: "100%" }}
						loading={servicesLoading}
						onChange={(serviceCode) => updateOrderItem(record.key, "serviceCode", serviceCode)}>
						{services.map((service) => (
							<Option key={service.code} value={service.code}>
								{service.name} ({service.code})
							</Option>
						))}
					</Select>
				);
			},
		},
		{
			title: (record) => (record?.type === "DOMAIN" ? "Domain Name" : "Plan"),
			dataIndex: "planOrDomain",
			key: "planOrDomain",
			width: 240,
			render: (_, record) => {
				if (record.type === "DOMAIN") {
					return (
						<Input
							value={record.domainName}
							placeholder="example.com"
							onChange={(e) =>
								updateOrderItem(record.key, "domainName", e.target.value.toLowerCase())
							}
						/>
					);
				}
				return (
					<Select
						value={record.planCode}
						placeholder="Select plan"
						style={{ width: "100%" }}
						disabled={!record.serviceCode}
						onChange={(planCode) => updateOrderItem(record.key, "planCode", planCode)}>
						{getServicePlans(record.serviceCode).map((plan) => (
							<Option key={plan.code} value={plan.code}>
								{plan.name} ({plan.code})
							</Option>
						))}
					</Select>
				);
			},
		},
		{
			title: "Region",
			dataIndex: "regionCode",
			key: "regionCode",
			width: 180,
			render: (value, record) =>
				record.type === "DOMAIN" ? (
					<Typography.Text type="secondary">-</Typography.Text>
				) : (
					<Select
						value={value}
						placeholder="Select region"
						style={{ width: "100%" }}
						disabled={!record.serviceCode}
						onChange={(regionCode) => updateOrderItem(record.key, "regionCode", regionCode)}>
						{getServiceRegions(record.serviceCode).map((region) => (
							<Option key={region.code} value={region.code}>
								{region.name} ({region.code})
							</Option>
						))}
					</Select>
				),
		},
		{
			title: (record) => (record?.type === "DOMAIN" ? "Years" : "Quantity"),
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			render: (value, record) => (
				<Space direction="vertical" size={0}>
					<InputNumber
						value={value}
						min={1}
						onChange={(quantity) => updateOrderItem(record.key, "quantity", quantity)}
					/>
					{record.type === "INSTANCE" && record.planCode ? (
						<Typography.Text type="secondary" style={{ fontSize: "12px" }}>
							per{" "}
							{getServicePlans(record.serviceCode).find((p) => p.code === record.planCode)?.price
								?.interval || "month"}
						</Typography.Text>
					) : null}
				</Space>
			),
		},
		{
			title: "Add-ons",
			dataIndex: "addons",
			key: "addons",
			width: 200,
			render: (value, record) => {
				if (record.type === "DOMAIN") {
					return <Typography.Text type="secondary">N/A</Typography.Text>;
				}
				const availableAddons = getServiceAddons(record.serviceCode);
				const selectedAddons = value || [];

				if (availableAddons.length === 0) {
					return <Typography.Text type="secondary">No add-ons available</Typography.Text>;
				}

				const AddonSelector = () => (
					<div style={{ width: 300 }}>
						<Checkbox.Group
							style={{ width: "100%", marginTop: 8 }}
							value={selectedAddons.map((addon) => addon.code)}
							onChange={(checkedValues) => {
								const newAddons = checkedValues
									.map(
										(code) =>
											availableAddons.find((addon) => addon.code === code) ||
											selectedAddons.find((addon) => addon.code === code)
									)
									.filter(Boolean);
								updateItemAddons(record.key, newAddons);
							}}>
							<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
								{availableAddons.map((addon) => (
									<div key={addon.code} style={{ display: "flex", flexDirection: "column" }}>
										<Checkbox value={addon.code}>
											<span style={{ fontWeight: "500" }}>{addon.name}</span>
										</Checkbox>
										<Typography.Text type="secondary" style={{ fontSize: "12px", marginLeft: 24 }}>
											{addon.description}
										</Typography.Text>
									</div>
								))}
							</div>
						</Checkbox.Group>
					</div>
				);

				return (
					<Popover
						content={<AddonSelector />}
						title="Add-ons"
						trigger="click"
						disabled={!record.serviceCode}>
						<Button type="link" disabled={!record.serviceCode}>
							{selectedAddons.length > 0 ? (
								<Space>
									<span>{selectedAddons.length} selected</span>
									{selectedAddons.map((addon) => (
										<Tag key={addon.code} size="small">
											{addon.name}
										</Tag>
									))}
								</Space>
							) : (
								"Select add-ons"
							)}
						</Button>
					</Popover>
				);
			},
		},
		{
			title: "Domain",
			dataIndex: "metadata",
			key: "metadata",
			width: 200,
			render: (_, record) => {
				if (record.type === "DOMAIN") {
					return <Typography.Text type="secondary">N/A</Typography.Text>;
				}
				
				return (
					<Input
						value={record.metadata?.domain || ""}
						placeholder="Enter domain for instance"
						onChange={(e) => updateItemMetadata(record.key, "domain", e.target.value)}
						style={{ width: "100%" }}
					/>
				);
			},
		},
		{
			title: "Subtotal",
			dataIndex: "price",
			key: "price",
			width: 120,
			align: "right",
			render: (_, record) => {
				const currency = form.getFieldValue("currency") || "USD";
				const itemPrice = calculateItemPrice(record);

				const valid =
					record.type === "DOMAIN"
						? record.extensionCode && record.domainName
						: record.serviceCode && record.planCode;

				if (!valid) {
					return <Typography.Text type="secondary">-</Typography.Text>;
				}

				return (
					<div style={{ textAlign: "right" }}>
						<Typography.Text strong>{formatCurrency(itemPrice, currency)}</Typography.Text>
						{record.quantity > 1 && (
							<div style={{ fontSize: "11px", color: "#666" }}>
								{formatCurrency(itemPrice / record.quantity, currency)} each
							</div>
						)}
					</div>
				);
			},
		},
		{
			title: "",
			key: "action",
			align: "right",
			width: 24,
			render: (_, record) => (
				<Button
					type="text"
					danger
					icon={<PiTrashDuotone />}
					onClick={() => removeOrderItem(record.key)}
					disabled={orderItems.length === 1}
				/>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/servers")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Add New Order
					</Title>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card title="Order Information">
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						initialValues={{
							currency: "IDR",
							metadata: "{}",
						}}>
						<Divider orientationMargin={0} orientation="left" plain>
							<strong>Basic Information</strong>
						</Divider>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="Team"
									name="teamId"
									rules={[
										{
											required: true,
											message: "Please select a team",
										},
									]}>
									<Select
										placeholder="Select team"
										loading={teamsLoading}
										showSearch
										filterOption={(input, option) =>
											option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
										}
										onChange={handleTeamSelect}>
										{teams.map((team) => (
											<Option key={team._id} value={team._id}>
												{`${team.name}<${team.owner.email}>`}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Currency"
									name="currency"
									rules={[
										{
											required: true,
											message: "Please select currency",
										},
									]}>
									<Select placeholder="Select currency">
										<Option value="IDR">Indonesian Rupiah (IDR)</Option>
										<Option value="SGD">Singapore Dollar (SGD)</Option>
										<Option value="AUD">Australian Dollar (AUD)</Option>
										<Option value="USD">US Dollar (USD)</Option>
										<Option value="EUR">Euro (EUR)</Option>
										<Option value="GBP">British Pound (GBP)</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Divider orientationMargin={0} orientation="left" plain>
							<strong>Order Items</strong>
						</Divider>

						<div style={{ marginBottom: 16 }}>
							<Button
								type="dashed"
								icon={<PiPlusDuotone />}
								onClick={addOrderItem}
								style={{ width: "100%" }}>
								Add Order Item
							</Button>
						</div>

						<Table
							columns={orderItemColumns}
							dataSource={orderItems}
							pagination={false}
							rowKey="key"
							size="small"
							rowClassName={() => "ant-table-row-top-align"}
							style={{ marginBottom: 24 }}
						/>

						{/* Pricing Summary */}
						{orderItems.some((item) => item.serviceCode && item.planCode) && (
							<Card
								size="small"
								style={{ marginBottom: 24, backgroundColor: "#fafafa" }}
								title={
									<Space>
										<Typography.Text strong>Order Summary</Typography.Text>
									</Space>
								}>
								{(() => {
									const summary = calculateOrderSummary();
									return (
										<Row gutter={16}>
											<Col span={16}>
												<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
													{orderItems
														.filter(
															(item) =>
																(item.type === "INSTANCE" && item.serviceCode && item.planCode) ||
																(item.type === "DOMAIN" && item.extensionCode && item.domainName)
														)
														.map((item) => {
															const service = services.find((s) => s.code === item.serviceCode);
															const plan = service?.plans?.find((p) => p.code === item.planCode);
															const region = item.regionCode
																? service?.regions?.find((r) => r.code === item.regionCode)
																: null;
															const itemPrice = calculateItemPrice(item);

															// Calculate detailed pricing breakdown
															const basePrice = plan?.price.amount || 0;
															const basePriceTotal = basePrice * (item.quantity || 1);

															let regionPrice = 0;
															let regionPriceTotal = 0;
															if (region?.price) {
																if (region.price.type === "FIXED") {
																	regionPrice = region.price.amount;
																	regionPriceTotal = regionPrice * (item.quantity || 1);
																} else if (region.price.type === "PERCENTAGE") {
																	regionPrice = basePriceTotal * (region.price.amount / 100);
																	regionPriceTotal = regionPrice;
																}
															}

															const priceAfterRegion = basePriceTotal + regionPriceTotal;

															let totalAddonPrice = 0;
															const addonBreakdown = [];
															if (item.addons && item.addons.length > 0) {
																item.addons.forEach((addon) => {
																	if (addon.price) {
																		let addonPrice = 0;
																		if (addon.price.type === "FIXED") {
																			addonPrice = addon.price.amount * (item.quantity || 1);
																		} else if (addon.price.type === "PERCENTAGE") {
																			addonPrice = priceAfterRegion * (addon.price.amount / 100);
																		}
																		totalAddonPrice += addonPrice;
																		addonBreakdown.push({
																			name: addon.name,
																			price: addonPrice,
																			type: addon.price.type,
																			amount: addon.price.amount,
																		});
																	}
																});
															}

															if (item.type === "DOMAIN") {
																const extension = domainExtensions.find(
																	(d) => d.code === item.extensionCode
																);
																const base = extension?.price?.registration?.amount || 0;
																const baseTotal = base * (item.quantity || 1);
																return (
																	<div
																		key={item.key}
																		style={{
																			padding: "12px",
																			border: "1px solid #f0f0f0",
																			borderRadius: "6px",
																			backgroundColor: "white",
																		}}>
																		<div style={{ marginBottom: "8px" }}>
																			<Typography.Text strong style={{ fontSize: "14px" }}>
																				Domain: {item.domainName} ({extension?.name})
																				{item.quantity > 1 && <span> x{item.quantity} yrs</span>}
																			</Typography.Text>
																		</div>
																		<div style={{ marginLeft: "12px", fontSize: "12px" }}>
																			<div
																				style={{
																					display: "flex",
																					justifyContent: "space-between",
																					marginBottom: 4,
																				}}>
																				<span style={{ color: "#666" }}>
																					Registration Price
																					{item.quantity > 1
																						? ` (${formatCurrency(base, summary.currency)} x ${
																								item.quantity
																						  })`
																						: ""}
																					:
																				</span>
																				<span>{formatCurrency(baseTotal, summary.currency)}</span>
																			</div>
																			<div
																				style={{
																					display: "flex",
																					justifyContent: "space-between",
																					marginTop: 8,
																					paddingTop: 8,
																					borderTop: "1px solid #f0f0f0",
																				}}>
																				<Typography.Text strong style={{ fontSize: 13 }}>
																					Line Total:
																				</Typography.Text>
																				<Typography.Text
																					strong
																					style={{ fontSize: 13, color: "#1890ff" }}>
																					{formatCurrency(itemPrice, summary.currency)}
																				</Typography.Text>
																			</div>
																		</div>
																	</div>
																);
															}
															return (
																<div
																	key={item.key}
																	style={{
																		padding: "12px",
																		border: "1px solid #f0f0f0",
																		borderRadius: "6px",
																		backgroundColor: "white",
																	}}>
																	<div style={{ marginBottom: "8px" }}>
																		<Typography.Text strong style={{ fontSize: "14px" }}>
																			{service?.name} - {plan?.name}
																			{item.quantity > 1 && <span> x{item.quantity}</span>}
																		</Typography.Text>
																	</div>

																	{/* Pricing Breakdown */}
																	<div style={{ marginLeft: "12px", fontSize: "12px" }}>
																		{/* Base Price */}
																		<div
																			style={{
																				display: "flex",
																				justifyContent: "space-between",
																				marginBottom: "4px",
																			}}>
																			<span style={{ color: "#666" }}>
																				Base Price{" "}
																				{item.quantity > 1
																					? `(${formatCurrency(basePrice, summary.currency)} x ${
																							item.quantity
																					  })`
																					: ""}
																				:
																			</span>
																			<span>
																				{formatCurrency(basePriceTotal, summary.currency)}
																			</span>
																		</div>

																		{/* Region Price */}
																		{region?.price && (
																			<div
																				style={{
																					display: "flex",
																					justifyContent: "space-between",
																					marginBottom: "4px",
																				}}>
																				<span style={{ color: "#666" }}>
																					Region ({region.name})
																					{/* {" "}-{" "}
																					{region.price.amount > 0
																						? region.price.type === "FIXED"
																							? "Fixed"
																							: `${region.price.amount}%`
																						: "Free"} */}
																				</span>
																				<span style={{ color: "#1890ff" }}>
																					{/* +{formatCurrency(regionPriceTotal, summary.currency)} */}
																					{regionPriceTotal > 0 ? (
																						<span>
																							+{formatCurrency(regionPriceTotal, summary.currency)}
																						</span>
																					) : (
																						<span>Free</span>
																					)}
																				</span>
																			</div>
																		)}

																		{/* Addons Price */}
																		{addonBreakdown.length > 0 &&
																			addonBreakdown.map((addon, index) => (
																				<div
																					key={index}
																					style={{
																						display: "flex",
																						justifyContent: "space-between",
																						marginBottom: "4px",
																					}}>
																					<span style={{ color: "#666" }}>
																						{addon.name}
																						{/* {" "}-{" "}
																						{addon.type === "FIXED" ? "Fixed" : `${addon.amount}%`}: */}
																					</span>
																					<span style={{ color: "#52c41a" }}>
																						+{formatCurrency(addon.price, summary.currency)}
																					</span>
																				</div>
																			))}

																		{/* Line Total */}
																		<div
																			style={{
																				display: "flex",
																				justifyContent: "space-between",
																				marginTop: "8px",
																				paddingTop: "8px",
																				borderTop: "1px solid #f0f0f0",
																			}}>
																			<Typography.Text strong style={{ fontSize: "13px" }}>
																				Line Total:
																			</Typography.Text>
																			<Typography.Text
																				strong
																				style={{ fontSize: "13px", color: "#1890ff" }}>
																				{formatCurrency(itemPrice, summary.currency)}
																			</Typography.Text>
																		</div>
																	</div>
																</div>
															);
														})}
												</div>
											</Col>
											<Col span={8}>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: 8,
														textAlign: "right",
													}}>
													<div style={{ display: "flex", justifyContent: "space-between" }}>
														<Typography.Text>Subtotal:</Typography.Text>
														<Typography.Text>
															{formatCurrency(summary.subtotal, summary.currency)}
														</Typography.Text>
													</div>
													{summary.setupFees > 0 && (
														<div style={{ display: "flex", justifyContent: "space-between" }}>
															<Typography.Text>Setup Fees:</Typography.Text>
															<Typography.Text>
																{formatCurrency(summary.setupFees, summary.currency)}
															</Typography.Text>
														</div>
													)}
													<Divider style={{ margin: "8px 0" }} />
													<div style={{ display: "flex", justifyContent: "space-between" }}>
														<Typography.Text strong style={{ fontSize: "16px" }}>
															Total:
														</Typography.Text>
														<Typography.Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
															{formatCurrency(summary.total, summary.currency)}
														</Typography.Text>
													</div>
													<Typography.Text type="secondary" style={{ fontSize: "12px" }}>
														{summary.itemCount} item{summary.itemCount !== 1 ? "s" : ""}
													</Typography.Text>
												</div>
											</Col>
										</Row>
									);
								})()}
							</Card>
						)}

						<Divider orientationMargin={0} orientation="left" plain>
							<strong>Billing Address</strong>
							{selectedTeamOwner && (
								<Typography.Text type="secondary" style={{ fontSize: "12px", marginLeft: 8 }}>
									(Auto-filled from team owner: {selectedTeamOwner.name})
								</Typography.Text>
							)}
						</Divider>

						<Row gutter={16}>
							<Col span={8}>
								<Form.Item
									label="Full Name"
									name="billingName"
									rules={[
										{
											required: true,
											message: "Please enter billing name",
										},
									]}>
									<Input placeholder="Enter billing name" />
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									label="Email Address"
									name="billingEmail"
									rules={[
										{
											required: true,
											message: "Please enter email address",
										},
										{
											type: "email",
											message: "Please enter valid email address",
										},
									]}>
									<Input placeholder="Enter email address" />
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									label="Phone Number"
									name="billingPhone"
									rules={[
										{
											required: true,
											message: "Please enter phone number",
										},
									]}>
									<Input placeholder="Enter phone number" />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={24}>
								<Form.Item
									label="Address"
									name="billingAddress"
									rules={[
										{
											required: true,
											message: "Please enter address",
										},
									]}>
									<Input placeholder="Enter street address" />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={24}>
							<Col span={6}>
								<Form.Item
									label="City"
									name="billingCity"
									rules={[
										{
											required: true,
											message: "Please enter city",
										},
									]}>
									<Input placeholder="Enter city" />
								</Form.Item>
							</Col>
							<Col span={6}>
								<Form.Item
									label="State/Province"
									name="billingState"
									rules={[
										{
											required: true,
											message: "Please enter state",
										},
									]}>
									<Input placeholder="Enter state" />
								</Form.Item>
							</Col>
							<Col span={6}>
								<Form.Item
									label="Postal Code"
									name="billingPostalCode"
									rules={[
										{
											required: true,
											message: "Please enter postal code",
										},
									]}>
									<Input placeholder="Enter postal code" />
								</Form.Item>
							</Col>
							<Col span={6}>
								<Form.Item
									label="Country"
									name="billingCountry"
									rules={[
										{
											required: true,
											message: "Please enter country",
										},
									]}>
									<Input placeholder="Enter country" />
								</Form.Item>
							</Col>
						</Row>

						<Divider orientationMargin={0} orientation="left" plain>
							<strong>Additional Information</strong>
						</Divider>

						<Row gutter={24}>
							<Col span={24}>
								<Form.Item
									label="Metadata (JSON)"
									name="metadata"
									rules={[{ validator: validateJSON }]}
									extra="Additional data in JSON format (e.g., credentials, configurations)">
									<TextArea
										rows={4}
										placeholder='{"username": "root", "password": "password123"}'
									/>
								</Form.Item>
							</Col>
						</Row>

						<Space style={{ marginTop: 24, width: "100%", justifyContent: "end" }}>
							<Button onClick={() => navigate("/orders")}>Cancel</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={loading}
								icon={<PiShoppingCartDuotone />}>
								Create Order
							</Button>
						</Space>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default AddOrderPage;
